import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Box, CircularProgress, Modal } from "@mui/material";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

/**
 * Delete confirmation modal — matches AccountDetail delete dialog styling.
 */
const ConfirmDeleteModal = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel = "Delete",
    deletingLabel = "Deleting...",
    width = 480,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!open) {
            setIsDeleting(false);
        }
    }, [open]);

    const handleClose = () => {
        if (isDeleting) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            await onConfirm();
            onClose();
        } catch (error) {
            // Caller handles errors (e.g. snackbar); keep modal open for retry.
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="confirm-delete-modal-title"
            aria-describedby="confirm-delete-modal-description"
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width,
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <Box className="pearl-popup-header"
                    sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 4,
                        py: 3,
                    }}
                >
                    <MDTypography
                        id="confirm-delete-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: "#FFFFFF" }}
                    >
                        {title}
                    </MDTypography>
                </Box>
                <Box sx={{ p: 4 }}>
                    <MDTypography
                        id="confirm-delete-modal-description"
                        variant="body1"
                        component="div"
                        mb={3}
                        sx={{ color: "#34495e", fontSize: "16px", lineHeight: 1.6 }}
                    >
                        {message}
                    </MDTypography>
                    <MDBox display="flex" justifyContent="flex-end">
                        <MDButton
                            onClick={handleClose}
                            variant="outlined"
                            color="secondary"
                            disabled={isDeleting}
                            sx={{ marginRight: 2 }}
                        >
                            Cancel
                        </MDButton>
                        <MDButton
                            onClick={handleConfirm}
                            variant="gradient"
                            color="error"
                            disabled={isDeleting}
                            startIcon={
                                isDeleting
                                    ? <CircularProgress size={20} color="inherit" />
                                    : null
                            }
                            sx={{
                                px: 3,
                                py: 1.5,
                                fontSize: "14px",
                                fontWeight: "600",
                                textTransform: "none",
                            }}
                        >
                            {isDeleting ? deletingLabel : confirmLabel}
                        </MDButton>
                    </MDBox>
                </Box>
            </Box>
        </Modal>
    );
};

ConfirmDeleteModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.node.isRequired,
    confirmLabel: PropTypes.string,
    deletingLabel: PropTypes.string,
    width: PropTypes.number,
};

export default ConfirmDeleteModal;
